import React, { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import * as Sentry from '@sentry/react'
import { Drawer } from '@/components/shared/Drawer'
import Input from '@/components/shared/molecules/CustomInput'
import { Button } from '@/components/shared/atoms/Button'
import { ChooseFileBottomSheet } from '@/components/shared/bottom-sheets/ChooseFileBottomSheet'
import { useCreateProduct } from '@/hooks/api/useProducts'
import { useGetProductImagePresignUrl } from '@/hooks/api/useUploads'
import { useImageGrid } from '@/hooks/useImageGrid'
import SortableImageGrid from '@/components/products/image-management/SortableImageGrid'
import type {
    CreateProductRequest,
    ProductDto,
    ScrapedProductDto,
} from '@wtf/shared'
import CurrencyInput from '@/components/shared/CurrencyInput'
import { classNames } from '@/utils/classNames'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'

const { logger } = Sentry

const styleOptions = ['menswear', 'womenswear', 'unisex']

const createProductSchema = z.object({
    name: z.string().min(1, 'Product name is required'),
    brand: z.string().min(1, 'Brand is required'),
    price: z.number().min(1, 'Price must be positive'),
    currency: z.string().min(1, 'Currency is required'),
    style: z.string().min(1, 'Style is required'),
    images: z
        .array(z.string())
        .min(1, { message: 'Please add at least one image' })
        .max(10, { message: 'Maximum 10 images allowed' })
        .refine((images) => images.length > 0, {
            message: 'Please add at least one image',
        }),
})

type CreateProductForm = z.infer<typeof createProductSchema>

export type CreateProductDetailsBottomSheetProps = {
    open: boolean
    onClose: () => void
    onAddToCollectionOpen?: () => void
    onNewProductClose?: () => void
    productData: ScrapedProductDto
    lookId?: string
    collectionId?: string
    onProductCreated: (product: ProductDto) => void
}

export function CreateProductDetailsBottomSheet({
    open,
    onClose,
    onAddToCollectionOpen,
    onNewProductClose,
    productData,
    lookId,
    collectionId,
    onProductCreated,
}: CreateProductDetailsBottomSheetProps) {
    const [openChooseFile, setOpenChooseFile] = useState(false)
    const posthog = usePostHog()

    // Form management
    const form = useForm<CreateProductForm>({
        resolver: zodResolver(createProductSchema),
        mode: 'onChange', // Enable real-time validation
        defaultValues: {
            name: productData.name ?? '',
            brand: productData.brand ?? '',
            price: productData.price ?? 0,
            style:
                (productData.gender?.toLowerCase() as string) ||
                styleOptions[0],
            images: productData.images ?? [],
            currency: productData.currency ?? 'USD',
        },
    })

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setValue,
        watch,
        reset,
    } = form

    // Memoize the onChange callback to prevent useImageGrid re-initialization
    const handleImageGridChange = useCallback(
        (urls: string[]) => {
            setValue('images', urls, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            })
        },
        [setValue]
    )

    // API hooks
    const { mutateAsync: createProduct, isPending } = useCreateProduct()
    const { mutateAsync: getPresignUrl } = useGetProductImagePresignUrl()

    // Use shared hook for image grid state and logic
    const {
        imageItems,
        uploadProgress,
        uploadErrors,
        isUploading,
        hasExcessImages,
        clearErrors,
        uploadImages,
        removeImage,
        activeImageId,
        onDragStart,
        onDragEnd,
        onDragCancel,
        isInitialized,
    } = useImageGrid({
        initialUrls: (productData.images ?? []).slice(0, 8),
        getPresign: getPresignUrl,
        onChange: handleImageGridChange,
        maxImages: 10,
        minImages: 1,
        skipUploadForExisting: false, // Scraped images need to be uploaded to our storage
    })

    // Initialize form images when grid is ready
    useEffect(() => {
        if (isInitialized && imageItems.length > 0) {
            const imageUrls = imageItems.map((item) => item.url)
            setValue('images', imageUrls, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            })
        }
    }, [isInitialized, imageItems, setValue])

    // Watch images for real-time updates
    const watchedImages = watch('images') || []
    const isFormValid =
        !hasExcessImages &&
        watchedImages.length >= 1 &&
        isValid &&
        isInitialized

    const handleImageUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files
            if (!files) return

            setOpenChooseFile(false)
            clearErrors()

            await uploadImages(files)
        },
        [uploadImages, clearErrors]
    )

    const onSubmit = async (data: CreateProductForm) => {
        try {
            // CRITICAL: Prevent submission while images are still uploading
            if (isUploading) {
                logger.warn('[CreateProduct] Prevented submission - images still uploading', {
                    imageCount: imageItems.length,
                })
                alert(
                    'Please wait for image upload to complete before creating the product.'
                )
                return
            }

            // Check if URLs are external URLs
            const areExternalURLs = data.images.some(
                (url) =>
                    url.includes('acnestudios.com') ||
                    url.includes('shopify.com') ||
                    url.includes('amazonaws.com') ||
                    url.includes('cloudfront.net') ||
                    url.includes('miumiu.com') ||
                    (!url.includes('wtf.style') &&
                        !url.includes('whatthefuckareyouwearing.com') && // Legacy domain support
                        !url.includes('r2.'))
            )

            // CRITICAL: Prevent submission if external URLs are detected
            if (areExternalURLs) {
                logger.error('[CreateProduct] External URLs detected in submission', {
                    imageCount: data.images.length,
                    urls: data.images,
                })
            }

            const createData: CreateProductRequest = {
                purchaseUrl: productData.purchaseUrl!,
                name: data.name,
                description: productData.description || '',
                brand: data.brand,
                price: data.price,
                currency: data.currency,
                gender: data.style.toUpperCase() as CreateProductRequest['gender'],
                images: data.images.map((url) => ({ url })),
                ...(lookId ? { lookId } : {}),
                ...(collectionId ? { collectionId } : {}),
            }

            const newProduct = await createProduct(createData)

            posthog?.capture(AnalyticsEvents.PRODUCT_IMPORT_COMPLETED, {
                product_id: newProduct.id,
                has_look_id: !!lookId,
                has_collection_id: !!collectionId,
                image_count: data.images.length,
            })

            onProductCreated(newProduct)

            // Only open add-to-collection sheet if product wasn't created from within a specific look/collection
            if (!lookId && !collectionId) {
                onAddToCollectionOpen?.()
                // Close the details sheet only and reset form state
                reset()
                clearErrors()
                onClose()
            } else {
                // Product was added to specific look/collection, just close
                handleClose()
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: { component: 'CreateProductDetailsBottomSheet', action: 'create_product' },
            })
        }
    }

    const handleClose = useCallback(() => {
        reset()
        clearErrors()
        onClose()
        onNewProductClose?.()
    }, [reset, clearErrors, onClose, onNewProductClose])

    return (
        <>
            <Drawer
                triggerContent={null}
                open={open}
                onOpenChange={handleClose}
                contentClassName=""
                heightMode="fixed"
                fixedHeight="90dvh"
                drawerHeader={{
                    showClose: true,
                    title: 'Create Product',
                    showArrow: false,
                    onClick: handleClose,
                }}
            >
                <div className="h-[calc(90dvh-120px)] overflow-y-auto">
                    <Input
                        label="Brand"
                        placeholder="Enter brand"
                        {...register('brand')}
                        error={errors.brand?.message}
                    />

                    <CurrencyInput
                        containerClassName="md:mt-2 mt-4"
                        label="Price"
                        defaultCurrency={productData.currency}
                        defaultValue={productData.price}
                        onChangeCurrency={(curr: string) => {
                            setValue('currency', curr, {
                                shouldValidate: true,
                                shouldDirty: true,
                            })
                        }}
                        onChangeInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setValue('price', Number(String(e.target.value)), {
                                shouldValidate: true,
                                shouldDirty: true,
                            })
                        }}
                        error={errors.price?.message || errors.currency?.message}
                    />

                    <Input
                        containerClassName="mt-4 md:mt-2"
                        label="Product Name"
                        placeholder="e.g., Oversized Wool Sweater"
                        {...register('name')}
                        error={errors.name?.message}
                    />

                    <div className="mt-4 md:mt-2">
                        <h2 className="mb-2 text-sm text-[#858585]">Style</h2>
                        <div className="flex w-full space-x-1">
                            {styleOptions.map((name) => (
                                <Button
                                    key={name}
                                    className="border border-black"
                                    variant={
                                        name === watch('style')
                                            ? 'solid'
                                            : 'outline'
                                    }
                                    onClick={() => {
                                        setValue('style', name, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        })
                                    }}
                                >
                                    {name}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 md:mt-2">
                        <h2 className="mb-2 text-sm text-[#858585]">
                            Images {imageItems.length}/10
                        </h2>

                        {/* Upload errors display */}
                        {uploadErrors.length > 0 && (
                            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
                                <p className="mb-2 text-sm font-medium text-red-600">
                                    Upload errors:
                                </p>
                                {uploadErrors.map((error, index) => (
                                    <p key={index} className="text-xs text-red-500">
                                        {error.fileName}: {error.error}
                                    </p>
                                ))}
                            </div>
                        )}

                        {hasExcessImages && (
                            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
                                <p className="text-sm font-medium text-red-600">
                                    Too many images ({imageItems.length}/10)
                                </p>
                                <p className="mt-1 text-xs text-red-500">
                                    Please remove {imageItems.length - 10} image(s)
                                    to continue. Maximum 10 images allowed.
                                </p>
                            </div>
                        )}

                        <SortableImageGrid
                            items={imageItems}
                            uploadProgress={uploadProgress}
                            isUploading={isUploading}
                            hasExcessImages={hasExcessImages}
                            hasError={uploadErrors.length > 0}
                            onAddClick={() => setOpenChooseFile(true)}
                            onRemove={removeImage}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onDragCancel={onDragCancel}
                            activeImageId={activeImageId}
                            minImages={1}
                        />
                    </div>
                </div>

                <div className="sticky bottom-0 z-10 w-full border-t border-gray-200 bg-white px-2 pt-3 pb-4">
                    <div className="flex space-x-2">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1 whitespace-nowrap bg-white text-sm font-medium"
                            disabled={isPending || isUploading}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>

                        <Button
                            className={classNames(
                                'flex-1 whitespace-nowrap border-black text-sm font-medium',
                                !isFormValid &&
                                !isPending &&
                                !isUploading &&
                                'cursor-not-allowed opacity-50'
                            )}
                            disabled={isPending || !isFormValid || isUploading}
                            onClick={handleSubmit(onSubmit)}
                        >
                            {isPending
                                ? 'Creating...'
                                : isUploading
                                    ? 'Uploading...'
                                    : 'Create Product'}
                        </Button>
                    </div>
                </div>
            </Drawer>
            <ChooseFileBottomSheet
                open={openChooseFile}
                onClose={() => setOpenChooseFile(false)}
                onFileChange={handleImageUpload}
                showRemoveButton={false}
            />
        </>
    )
}
